import { useTranslation } from 'react-i18next'

import { Alignment } from '@/components/Table/tableHelper'
import { BucketType } from '@/schema'
import { formatDisplayDate, formatNumber } from '@/utils'
import { DATE_PICKER_FORMAT as BUCKET_DATE_FORMAT } from '@/utils/constants'

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../../../components/ui'
import { MONTH_FORMAT } from '../..'
import { TDetailDrawerColumn } from '../../types'

export const useProjectNameColumn = (): TDetailDrawerColumn => {
  const { t } = useTranslation()
  return {
    id: 'project_name',
    label: t('dashboard.columns.projectName'),
    width: 250,
    align: Alignment.LEFT,
    sortable: true,
    alignLabel: Alignment.LEFT,
    renderCell: (_, row) => {
      if ('project_name' in row) {
        return <span>{row.project_name}</span>
      }

      if ('deliverable_set_name' in row) {
        return <span>{row.deliverable_set_name}</span>
      }

      return '-'
    },
  }
}

export const useDeliverableSetColumn = (): TDetailDrawerColumn => {
  const { t } = useTranslation()
  return {
    id: 'ds_name',
    label: t('dashboard.columns.deliverableSet'),
    width: 250,
    align: Alignment.LEFT,
    sortable: true,
    alignLabel: Alignment.LEFT,
    renderCell: (value) => <span>{value ?? '-'}</span>,
  }
}

export const useNoOfProjectsColumns = (): TDetailDrawerColumn[] => {
  const { t } = useTranslation()
  const projectNameColumn = useProjectNameColumn()
  return [
    projectNameColumn,
    {
      id: 'active_deliverables',
      label: t('dashboard.columns.activeDeliverables'),
      width: 200,
      align: Alignment.RIGHT,
      sortable: true,
      alignLabel: Alignment.RIGHT,
      renderCell: (value) => <span>{value ? formatNumber(value) : '-'}</span>,
    },
    {
      id: 'completed_deliverables',
      label: t('dashboard.columns.completedDeliverables'),
      width: 230,
      align: Alignment.RIGHT,
      sortable: true,
      alignLabel: Alignment.RIGHT,
      renderCell: (value) => <span>{value ? formatNumber(value) : '-'}</span>,
    },
  ]
}

export const useLateDeliverablesColumns = (): TDetailDrawerColumn[] => {
  const { t } = useTranslation()
  const projectNameColumn = useProjectNameColumn()
  return [
    projectNameColumn,
    {
      id: 'deliverable_set_name',
      label: t('dashboard.columns.deliverableSet'),
      width: 200,
      align: Alignment.LEFT,
      sortable: true,
      alignLabel: Alignment.LEFT,
      renderCell: (value) => <span>{value ?? '-'}</span>,
    },
    {
      id: 'overdue_days',
      label: t('dashboard.columns.overdueDays'),
      width: 150,
      align: Alignment.RIGHT,
      sortable: true,
      alignLabel: Alignment.RIGHT,
      renderCell: (value) => {
        if (!value) return <span>-</span>
        const num = Number(value)
        return <span>{Number.isInteger(num) ? num : num.toFixed(1)}</span>
      },
    },
  ]
}

export const useLateDeliverablesColumnsForProjectLevel =
  (): TDetailDrawerColumn[] => {
    const { t } = useTranslation()
    return [
      {
        id: 'deliverable_set_name',
        label: t('dashboard.columns.deliverableSet'),
        width: 250,
        align: Alignment.LEFT,
        sortable: true,
        alignLabel: Alignment.LEFT,
        renderCell: (value) => <span>{value ?? '-'}</span>,
      },
      {
        id: 'ds_due_date',
        label: t('dashboard.columns.dueDate'),
        width: 150,
        align: Alignment.LEFT,
        sortable: true,
        alignLabel: Alignment.LEFT,
        renderCell: (value) => (
          <span>
            {value ? formatDisplayDate(value, BUCKET_DATE_FORMAT) : '-'}
          </span>
        ),
      },
      {
        id: 'overdue_days',
        label: t('dashboard.columns.overdueDays'),
        width: 150,
        align: Alignment.RIGHT,
        sortable: true,
        alignLabel: Alignment.RIGHT,
        renderCell: (value) => {
          if (!value) return <span>-</span>
          const num = Number(value)
          return <span>{Number.isInteger(num) ? num : num.toFixed(1)}</span>
        },
      },
    ]
  }

const useCommentDensityDefaultColumns = (): TDetailDrawerColumn[] => {
  const { t } = useTranslation()
  return [
    {
      id: 'no_of_comments',
      label: t('dashboard.columns.noOfComments'),
      width: 180,
      align: Alignment.RIGHT,
      sortable: true,
      alignLabel: Alignment.RIGHT,
      renderCell: (value) => <span>{value ? formatNumber(value) : '-'}</span>,
    },
    {
      id: 'no_of_files',
      label: t('dashboard.columns.noOfFiles'),
      width: 140,
      align: Alignment.RIGHT,
      sortable: true,
      alignLabel: Alignment.RIGHT,
      renderCell: (value) => <span>{value ? formatNumber(value) : '-'}</span>,
    },
    {
      id: 'comment_density',
      label: t('dashboard.columns.commentDensity'),
      width: 180,
      align: Alignment.RIGHT,
      sortable: true,
      alignLabel: Alignment.RIGHT,
      renderCell: (value) => {
        return (
          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>{value ? formatNumber(value) : '-'}</span>
              </TooltipTrigger>
              {!!value && (
                <TooltipContent>
                  <span>{`${formatNumber(value)} ${t('dashboard.units.commentPerFile')}`}</span>
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        )
      },
    },
  ]
}

export const useCommentDensityColumns = (): TDetailDrawerColumn[] => {
  const projectNameColumn = useProjectNameColumn()
  const commentDensityDefaultColumns = useCommentDensityDefaultColumns()
  return [projectNameColumn, ...commentDensityDefaultColumns]
}

export const useCommentDensityColumnsWithDeliverableSet =
  (): TDetailDrawerColumn[] => {
    const deliverableSetColumn = useDeliverableSetColumn()
    const commentDensityDefaultColumns = useCommentDensityDefaultColumns()
    return [deliverableSetColumn, ...commentDensityDefaultColumns]
  }

export const useCommentDensityColumnsForDeliverableLevel =
  (): TDetailDrawerColumn[] => {
    const commentDensityDefaultColumns = useCommentDensityDefaultColumns()
    // Remove deliverable set column and disable sorting for all columns
    return commentDensityDefaultColumns.map((column) => ({
      ...column,
      sortable: false,
    }))
  }

export const useAvgReviewTimeColumns = (): TDetailDrawerColumn[] => {
  const { t } = useTranslation()
  const deliverableSetColumn = useDeliverableSetColumn()
  return [
    { ...deliverableSetColumn, width: 300 },
    {
      id: 'cycle_time_days',
      label: t('dashboard.columns.cycleTimeDays'),
      width: 250,
      align: Alignment.RIGHT,
      sortable: true,
      alignLabel: Alignment.RIGHT,
      renderCell: (value) => <span>{value ? formatNumber(value) : '-'}</span>,
    },
  ]
}

export const useApprovedFilesColumns = (
  bucketType: BucketType
): TDetailDrawerColumn[] => {
  const { t } = useTranslation()
  return [
    {
      id: 'bucket_date',
      label:
        bucketType === BucketType.week
          ? t('dashboard.columns.weekBucket')
          : t('dashboard.columns.monthBucket'),
      width: 200,
      align: Alignment.LEFT,
      sortable: true,
      alignLabel: Alignment.LEFT,
      renderCell: (value) => (
        <span>
          {formatDisplayDate(
            value,
            bucketType === BucketType.month ? MONTH_FORMAT : BUCKET_DATE_FORMAT
          )}
        </span>
      ),
    },
    {
      id: 'approved_count',
      label: t('dashboard.columns.approvedFilesCount'),
      width: 250,
      align: Alignment.RIGHT,
      sortable: true,
      alignLabel: Alignment.RIGHT,
      renderCell: (value) => <span>{value ? formatNumber(value) : '-'}</span>,
    },
  ]
}
